/*
 * File: /src/store/modules/company.js
 * Created Date: Saturday March 16th 2019
 * Author: Nicolas Wegener
 * -----
 * Last Modified: Sunday, 17th March 2019 4:19:09 pm
 * Modified By: Nicolas Wegener (wegenern@gmail.com)
 * -----
 * Copyright (c) 2019 AdBase, Inc.
 */
import templateService from 'src/services/templateServices.js';
import emailTemplateServices from 'src/services/emailTemplateServices';

import blankEmail from 'src/assets/data/blank-email.json';
import blankPopup from 'src/assets/data/blank-popup.json';
import campaignBlocks from 'src/assets/data/campaign-block.json';

// Our state is mostly designated for a singleton. For the
// state control about templates, see templates. This is
// meant for our editor only.
const state = {
  id: undefined,
  showName: false,

  // Name, Subject & Preview
  name: '',
  subject: '',
  preview: '',

  // Main Building Objects
  backgroundElement: {},
  headerElements: [],
  bodyElements: [],
  footerElements: [],

  // Test Emails
  testEmails: [],
  testPhoneNumbers: [],

  // Editor Selected
  selectedBlock: undefined,
  selectedBlockSettings: {},
  selectedLayoutNode: undefined,

  // Google Fonts
  fontDefinitions: [],

  // Might be able to remove this appearance thingy...
  appearance: {
    emailWidth: 600,
    emailBackgroundColor: '#e6e7e8',
    emailBackgroundColor: '#f7f7f7',
    defaultPadding: { t: 0, r: 0, b: 0, l: 0 },
    defaultFont: {
      family: 'Arial',
      style: 'normal',
      size: '14px',
      color: '#333333',
    },
    linkColor: '#bf9000',
    lineSpacing: '1.0',
    underLineLinks: false,
  },

  // The Control Pane
  controlPane: {
    side: 'right',
    show: true,
  },

  colors: [],
};

const getters = {
  getColors: (state) => state.colors,
  // for sending
  setTemplateId: (state) => state.id,
  getTestEmails: (state) => state.testEmails,
  getTestPhoneNumbers: (state) => state.testPhoneNumbers,
  // For Settings
  getBackgroundColor: (state) => state.backgroundElement.background,
  getBackgroundColor: (state) => state.backgroundElement.background,
  getContentColor: (state) => state.backgroundElement.contentBackground,
  getModalWidth: (state) => state.backgroundElement.modalWidth || 300,
  getAdvancedEditor: (state) => state.backgroundElement.advancedEditor || false,
  getPageWidth: (state) => state.backgroundElement.width || 600,
  getDefaultFont: (state) => state.backgroundElement.font || 'Roboto',
  // Editor Needs
  getElementsByTypeIndex: (state) => (type, index) => {
    switch (type) {
      case 'header':
        return state.headerElements[index];
      case 'body':
        return state.bodyElements[index];
      case 'footer':
        return state.footerElements[index];
      case 'background':
        return state.backgroundElement;
    }
    return null;
  },
  getBlockByIndexColumn: (state) => (type, index, column) => {
    switch (type) {
      case 'header':
        return state.headerElements[index].blocks[column];
      case 'body':
        return state.bodyElements[index].blocks[column];
      case 'footer':
        return state.footerElements[index].blocks[column];
      case 'background':
        return state.backgroundElement;
    }
    return null;
  },
  getElementsByType: (state) => (type) => {
    switch (type) {
      case 'header':
        return state.headerElements;
      case 'body':
        return state.bodyElements;
      case 'footer':
        return state.footerElements;
      case 'background':
        return state.backgroundElement;
    }
    return null;
  },
  getBlockMeta: (state) => (meta) => {
    const obj = Object.assign([], state[meta.section + 'Elements']);
    let rObject = undefined;
    try {
      rObject = obj[meta.row].blocks[meta.column];
    } catch (e) {
      rObject = null;
    }
    return rObject;
  },

  getControlPaneShow: (state) => state.controlPane.show,
  getEditorName: (state) => state.name,
  getEditorSubject: (state) => state.subject,
  getEditorPreview: (state) => state.preview,
  getDecodedSelectedBlock: (state) => {
    if (state.selectedBlock === undefined) {
      return {
        section: 'background',
      };
    }
    if (state.selectedBlock.indexOf('-') === -1) {
      return {
        section: 'background',
      };
    }
    const splitObjectState = state.selectedBlock.split('-');
    return {
      section: splitObjectState[0],
      row: splitObjectState[1],
      column: splitObjectState[2],
    };
  },
  getSelectedBlockExists: (state) => {
    if (state.selectedBlock === undefined) {
      return false;
    }
    if (state.selectedBlock.indexOf('-') === -1) {
      return false;
    }
    return true;
  },
  getSelectedBlock: (state) => (section, row, column) => {
    if (state.selectedBlock === undefined) {
      return false;
    }
    if (state.selectedBlock.indexOf('-') === -1) {
      return false;
    }

    let tally = 0;
    const argsArray = [section, row, column];
    const splitObjectState = state.selectedBlock.split('-');
    for (const inc in splitObjectState) {
      if (parseInt(inc, 10) === 0 && argsArray[inc] === splitObjectState[inc]) {
        tally++;
      }
      if (
        parseInt(inc, 10) > 0 &&
        parseInt(argsArray[inc], 10) === parseInt(splitObjectState[inc], 10)
      )
        tally++;
    }
    if (tally === 3) {
      return true;
    }
    return false;
  },
  getSelectedBlockType: (state) => {
    if (state.selectedBlock === undefined) {
      return false;
    }
    if (state.selectedBlock.indexOf('-') === -1) {
      return false;
    }

    const splitObjectState = state.selectedBlock.split('-');
    const meta = {
      section: splitObjectState[0],
      row: splitObjectState[1],
      column: splitObjectState[2],
    };
    const obj = Object.assign([], state[meta.section + 'Elements']);
    let rObject = undefined;
    try {
      rObject = obj[meta.row].blocks[meta.column];
    } catch (e) {}

    if (rObject === undefined) return false;

    return rObject.type;
  },
  getSelectedLayoutNode: (state) => state.selectedLayoutNode,
  getFontDefinitions: (state) => state.fontDefinitions,
};

/**
 * Actions
 */
const actions = {
  clearAll({ commit }, type) {
    return new Promise((resolve, reject) => {
      let body = [];
      let background = [];

      if (type === 'popup') {
        body = blankPopup.body;
        background = blankPopup.background;
      }
      if (type === 'email') {
        body = blankEmail.body;
        background = blankEmail.background;
      }

      // We need this to reset the save id.
      commit('setTemplateId', 0);
      commit('setHeaderElements', []);
      commit('setBodyElements', body);
      commit('setFooterElements', []);
      commit('setBackgroundElement', background);
      commit('setName', '');
      commit('setSubject', '');
      commit('setPreview', '');
      resolve(true);
    });
  },
  startFromTemplate({ commit }, meta) {
    return new Promise((resolve, reject) => {
      // We need this to reset the save id.
      commit('setTemplateId', 0);
      commit('setHeaderElements', meta.header);
      commit('setBodyElements', meta.body);
      commit('setFooterElements', meta.footer);
      commit('setBackgroundElement', meta.background);
      commit('setName', '');
      commit('setSubject', '');
      commit('setPreview', '');
      resolve(true);
    });
  },
  addColor({ commit, state }, meta) {
    if (state.colors.indexOf(meta) > -1) return;
    const currentColors = Object.assign([], state.colors);
    currentColors.push(meta);
    commit('setColors', currentColors);
  },
  clearColors({ commit }) {
    const currentColors = Object.assign([]);
    commit('setColors', currentColors);
  },
  addTestEmail({ commit, state }, meta) {
    const currentEmails = Object.assign([], state.testEmails);
    currentEmails.push(meta);
    commit('setTestEmail', currentEmails);
  },
  addTestPhoneNumber({ commit, state }, meta) {
    const currentPhoneNumbers = Object.assign([], state.testPhoneNumbers);
    currentPhoneNumbers.push(meta);
    commit('setTestPhoneNumbers', currentPhoneNumbers);
  },
  removeTestMessageContact({ commit, state }, meta) {
    let found = false;
    return new Promise((resolve, reject) => {
      let currentList;
      let type = '';
      switch (meta.type) {
        case 'email':
          currentList = Object.assign([], state.testEmails);
          type = 'setTestEmail';
          break;
        case 'sms':
          currentList = Object.assign([], state.testPhoneNumbers);
          type = 'setTestPhoneNumbers';
          break;
      }
      const newList = [];
      for (const eachItem of currentList) {
        if (
          eachItem.email === meta.contact ||
          eachItem.phone === meta.contact
        ) {
          found = true;
        } else newList.push(eachItem);
      }
      commit(type, newList);
      resolve(found);
    });
  },
  changeTestMessageStatus({ commit, state }, meta) {
    let found = false;
    return new Promise((resolve, reject) => {
      let currentList;
      let type = '';
      switch (meta.type) {
        case 'email':
          currentList = Object.assign([], state.testEmails);
          type = 'setTestEmail';
          break;
        case 'sms':
          currentList = Object.assign([], state.testPhoneNumbers);
          type = 'setTestPhoneNumbers';
          break;
      }
      const newList = [];
      for (const eachItem of currentList) {
        if (
          eachItem.email === meta.contact ||
          eachItem.phone === meta.contact
        ) {
          found = true;
          const currentItem = Object.assign({}, eachItem);
          currentItem.send = eachItem.send === true ? false : true;
          newList.push(currentItem);
        } else newList.push(eachItem);
      }
      commit(type, newList);
      resolve(found);
    });
  },
  addStructureToType({ commit, state }, meta) {
    switch (meta.type) {
      case 'header':
        const curHeaderObj = Object.assign([], state.headerElements) || [];
        if (meta.useIndex) {
          curHeaderObj.splice(meta.index, 0, meta.obj);
        } else {
          curHeaderObj.push(meta.obj);
        }
        commit('setHeaderElements', curHeaderObj);
        break;
      case 'body':
        const curBodyObj = Object.assign([], state.bodyElements) || [];
        if (meta.useIndex) {
          curBodyObj.splice(meta.index, 0, meta.obj);
        } else {
          curBodyObj.push(meta.obj);
        }
        commit('setBodyElements', curBodyObj);
        break;
      case 'footer':
        const curFooterObj = Object.assign([], state.footerElements) || [];
        if (meta.useIndex) {
          curFooterObj.splice(meta.index, 0, meta.obj);
        } else {
          curFooterObj.push(meta.obj);
        }
        commit('setFooterElements', curFooterObj);
        break;
    }
  },
  addColumnToStructure({ commit, state }, meta) {
    const readTypeMap = {
      header: 'headerElements',
      body: 'bodyElements',
      footer: 'footerElements',
    };
    const typeMap = {
      header: 'setHeaderElements',
      body: 'setBodyElements',
      footer: 'setFooterElements',
    };

    const obj = Object.assign([], state[readTypeMap[meta.type]]) || [];
    for (const inc in obj) {
      if (parseInt(inc, 10) === parseInt(meta.row, 10)) {
        const newObj = Object.assign({}, obj[inc]);
        newObj.columns = new Array(newObj.columns.length + 1).fill(
          100 / (newObj.columns.length + 1)
        );
        newObj.blocks = [...newObj.blocks, { type: undefined }];
        obj[inc] = newObj;
        break;
      }
    }

    commit(typeMap[meta.type], obj);
  },
  removeColumnFromStructure({ commit, state }, meta) {
    const removeColumn = (col) => {
      const percent = 100 / (col.columns.length - 1);
      const newColumns = [];
      for (let i = 0; i < col.columns.length - 1; i++) {
        newColumns.push(percent);
      }
      return newColumns;
    };
    const removeBlock = (col, index) => {
      const adjustedBlocks = Object.assign([], col.blocks);
      adjustedBlocks.splice(index, 1);
      return adjustedBlocks;
    };
    switch (meta.type) {
      case 'header':
        const curHeaderObj = Object.assign([], state.headerElements) || [];
        for (const inc in curHeaderObj) {
          if (parseInt(inc, 10) === parseInt(meta.row, 10)) {
            const obj = Object.assign({}, curHeaderObj[inc]);
            obj.columns = removeColumn(obj);
            obj.blocks = removeBlock(obj, meta.column);
            curHeaderObj[inc] = Object.assign({}, obj);
          }
        }
        commit('setHeaderElements', curHeaderObj);
        break;
      case 'body':
        const curBodyObj = Object.assign([], state.bodyElements) || [];
        for (const inc in curBodyObj) {
          if (parseInt(inc, 10) === parseInt(meta.row, 10)) {
            const obj = Object.assign({}, curBodyObj[inc]);
            obj.columns = removeColumn(obj);
            obj.blocks = removeBlock(obj, meta.column);
            curBodyObj[inc] = Object.assign({}, obj);
          }
        }
        commit('setBodyElements', curBodyObj);
        break;
      case 'footer':
        const curFooterObj = Object.assign([], state.footerElements) || [];
        for (const inc in curFooterObj) {
          if (parseInt(inc, 10) === parseInt(meta.row, 10)) {
            const obj = Object.assign({}, curFooterObj[inc]);
            obj.columns = removeColumn(obj);
            obj.blocks = removeBlock(obj, meta.column);
            curFooterObj[inc] = Object.assign({}, obj);
          }
        }
        commit('setFooterElements', curFooterObj);
        break;
    }
  },
  addBlockToStructure({ commit, state }, meta) {
    switch (meta.type) {
      case 'header':
        const curHeaderObj = Object.assign([], state.headerElements) || [];
        curHeaderObj[meta.index].blocks[meta.column] = meta.obj;
        commit('setHeaderElements', curHeaderObj);
        break;
      case 'body':
        commit('addBodyElement', meta);
        break;
      case 'footer':
        const curFooterObj = Object.assign([], state.footerElements) || [];
        curFooterObj[meta.index].blocks[meta.column] = meta.obj;
        commit('setFooterElements', curFooterObj);
        break;
    }
  },
  showControlPane({ commit }, status) {
    if (status === undefined) status = true;
    return new Promise((resolve, reject) => {
      commit('setControlPaneShown', status);
      resolve(status);
    });
  },
  showNameField({ commit }, status) {
    if (status === undefined) status = true;
    return new Promise((resolve, reject) => {
      commit('setNameShown', status);
      resolve(status);
    });
  },
  removeStructureByRow({ commit, state }, meta) {
    return new Promise((resolve, reject) => {
      const typeCap = meta.type.charAt(0).toUpperCase() + meta.type.slice(1);
      const obj = Object.assign([], state[meta.type + 'Elements']);
      if (obj.length === 0) {
        reject('empty array');
      }
      const newObj = obj.splice(meta.row, 1);
      commit('set' + typeCap + 'Elements', obj);
      resolve(newObj);
    });
  },
  copyStructureByRow({ commit, state }, meta) {
    return new Promise((resolve, reject) => {
      const typeCap = meta.type.charAt(0).toUpperCase() + meta.type.slice(1);
      const obj = JSON.parse(JSON.stringify(state[meta.type + 'Elements'])); // deep copy
      if (obj.length === 0) {
        reject('empty array');
      }
      const newRow = duplicateArrayIndex(obj, parseInt(meta.row, 10));
      commit('set' + typeCap + 'Elements', newRow);
      resolve(newRow);
    });
  },
  moveStructureUpByRow({ commit, state }, meta) {
    return new Promise((resolve, reject) => {
      const typeCap = meta.type.charAt(0).toUpperCase() + meta.type.slice(1);
      const obj = Object.assign([], state[meta.type + 'Elements']);
      if (obj.length === 0) {
        reject('empty array');
      }
      const uObj = arrayMove(
        obj,
        parseInt(meta.row, 10),
        parseInt(meta.row, 10) - 1
      );
      commit('set' + typeCap + 'Elements', uObj);
      resolve(uObj);
    });
  },
  moveStructureDownByRow({ commit, state }, meta) {
    return new Promise((resolve, reject) => {
      const typeCap = meta.type.charAt(0).toUpperCase() + meta.type.slice(1);
      const obj = Object.assign([], state[meta.type + 'Elements']);
      if (obj.length === 0) {
        reject('empty array');
      }
      const uObj = arrayMove(
        obj,
        parseInt(meta.row, 10),
        parseInt(meta.row, 10) + 1
      );
      commit('set' + typeCap + 'Elements', uObj);
      resolve(uObj);
    });
  },
  moveStructureToRow({ commit, state }, meta) {
    return new Promise((resolve, reject) => {
      const rowType = meta.type.charAt(0).toUpperCase() + meta.type.slice(1);
      const obj = Object.assign([], state[meta.type + 'Elements']);
      const sourceRow = parseInt(meta.row, 10);
      const targetRow = parseInt(meta.toRow, 10);
      if (obj.length === 0 || isNaN(sourceRow) || isNaN(targetRow)) {
        reject('empty array or no source or target');
      }
      const updatedObject = arrayMove(obj, sourceRow, targetRow);
      commit('set' + rowType + 'Elements', updatedObject);
      resolve(updatedObject);
    });
  },
  storeBlockMeta({ commit }, meta) {
    return new Promise((resolve, reject) => {
      if (
        meta.data.type !== 'text' &&
        meta.data.type !== 'image' &&
        meta.data.type !== 'button' &&
        meta.data.type !== 'line' &&
        meta.data.type !== 'video' &&
        meta.data.type !== 'layout' &&
        meta.data.type !== 'canva'
      ) {
        reject('malformed block meta request');
      }
      commit('setBlockMeta', meta);
      resolve(true);
    });
  },
  saveTemplate({ commit, state }, meta) {
    return new Promise(async (resolve, reject) => {
      let templateType = meta.type || 'email';
      let isSystem = meta.system || false;
      if (+state.id === 0) {
        commit('setTemplateId', undefined);
      }

      let template;
      if (templateType == 'email') {
        const deliverable = {
          template_type: 'legacy',
          s3_uuid: '',
          name: state.name,
          subject: state.subject,
          preview: state.preview,
          required_tokens: '', // FIXME: contact,unit,reservation
          is_active: true,
          is_archive: false,
          is_system: isSystem,
          is_reply: false,
          is_campaign: false,
          is_automation: false,
          // FIXME: this will need to be revered to combine all bodyElements.
          legacy_json_content: {
            background: state.backgroundElement,
            header: state.headerElements,
            body: state.bodyElements,
            footer: state.footerElements,
          },
          thumbnail: '', // we push out an empty string, the BE will overwrite it.
        };
        template = isSystem
          ? await emailTemplateServices.systemTemplate(state.id, deliverable)
          : await emailTemplateServices.saveTemplate(state.id, deliverable);
        // template = await emailTemplateServices.saveTemplate(state.id, );
      } else {
        template = await templateService.saveTemplate(
          state.id,
          templateType,
          state.name,
          state.subject,
          state.preview,
          state.backgroundElement,
          state.headerElements,
          state.bodyElements,
          state.footerElements,
          isSystem
        );
        
      }
      // once we save... pull the id, and set it.  Now, we're updating!
      commit('setTemplateId', template.data.id);
      resolve(template.data.id);
    });
  },
  setEditorMeta({ commit }, meta) {
    return new Promise((resolve, reject) => {
      if (meta.name) {
        commit('setName', meta.name);
      }
      if (meta.subject) {
        commit('setSubject', meta.subject);
      }
      if (meta.preview) {
        commit('setPreview', meta.preview);
      }
      resolve(true);
    });
  },
  loadEditorById({ commit }, meta) {
    // Here we remove any footer or body elements from prior saves:
    //   When the editor was initially created; we allowed users to
    //   create header & footer elements.  The idea was that header
    //   body and footer elements could have different features in
    //   email, and have separate editing components.  Such as:
    //    - readonly.
    //    - header/footer micro templates.
    //    - different widths
    //    - alternating backgrounds
    //   All still features worth merit.  However, without focus,
    //   the header lost it's way.  As did the footer.
    // Sept of 2022:  We added additional tracking to unsubscribes
    //   and allow campaigns to be created in draft mode.  This also
    //   brought over a new series of checks to ensure templates
    //   and campaigns are up to our standard prior to sending.  To
    //   add we've brought back the 'footer' which is a static element
    //   that will store unsubscribe links, address and the "send with"
    //   logo.  By populating this on the load, we give the user the
    //   option to alter the contents of the footer to match their
    //   formatting and stylistic needs.

    return new Promise(async (resolve, reject) => {
      const service =
        meta.type == 'email' ? emailTemplateServices : templateService;
      const template = await service.fetchTemplate(meta.id);
      // if the json_content is malformed... let's not let it load.
      let json;
      try {
        json =
          meta.type == 'email'
            ? template.data.legacy_json_content
            : template.data.json_content;
      } catch (error) {
        console.error(error);
        reject(error);
      }

      // We are still collapsing the header.
      if (json.header.length > 0) {
        // Bring any header elements over to the body.
        const obj = [...json.header, ...json.body];
        json.body = Object.assign([], obj);
        json.header = undefined;
      }

      // Check to ensure the footer blocks have been previously set
      // by the campaign editor.  If they haven't, that means they
      // can't be trusted and should be removed.  The footer is a
      // special placeholder for campaigns.
      let addDefaultCampaignFooter = true;
      if (json.footer.length > 0) {
        // No longer bring the footer elements over to the body.
        // Do unset any prior footer alterations, as we will only
        // use this as a un-editable block.
        const blocks = json.footer.filter(
          (f) => f['block-type'] == 'unsubscribe-campaign'
        );
        // There are no
        if (blocks.length == 0) {
          json.footer = undefined;
        } else {
          // The user has customized the footer block.
          // don't override the field
          addDefaultCampaignFooter = false;
        }
      }

      // If email & campaign
      if (
        meta.emailType &&
        meta.emailType == 'campaign' &&
        addDefaultCampaignFooter
      ) {
        json.footer = campaignBlocks;
      }

      // Content
      commit('setBackgroundElement', json.background);
      commit('setHeaderElements', json.header);
      commit('setBodyElements', json.body);
      commit('setFooterElements', json.footer);
      // Names & Id
      commit('setTemplateId', parseInt(meta.id, 10));
      commit('setName', template.data.name);
      commit('setSubject', template.data.subject);
      commit('setPreview', template.data.preview);

      resolve({});
    });
  },

  loadEditorSubject({ commit }, meta) {
    return new Promise(async (resolve, reject) => {
      const template = await emailTemplateServices.fetchTemplate(meta.id);
      try {
        commit('setSubject', template.data.subject);
        commit('setPreview', template.data.preview);
        resolve({});
      } catch (error) {
        reject(error);
      }
    });
  },
  changeTemplateId({ commit }, id) {
    return new Promise((resolve, reject) => {
      commit('setTemplateId', +id);
      resolve(+id);
    });
  },

  setSelectedBlockUndefined({ commit }, meta) {
    return new Promise((resolve, reject) => {
      commit('setSelectedBlock', undefined);
      resolve({});
    });
  },

  selectBlock({ commit }, meta) {
    return new Promise((resolve, reject) => {
      commit(
        'setSelectedBlock',
        meta.section + '-' + meta.row + '-' + meta.column
      );
      resolve({});
    });
  },

  assignSelectedLayoutNode({ commit }, node) {
    return new Promise((resolve, reject) => {
      commit('setSelectedLayoutNode', node.name);
      resolve({});
    });
  },
  /**
   * Will transpose the block settings to the style
   * @param {*} param0
   * @param {*} meta
   */
  transportBlockSettings({ commit }, meta) {
    return new Promise((resolve, reject) => {
      if (!meta.section || !meta.row || !meta.column) {
        reject('Unable to set block settings');
      }
      commit('setBlockMeta', meta);
      resolve({});
    });
  },
  assignFontDefinitions({ commit }, fonts) {
    return new Promise((resolve, reject) => {
      commit('setFontDefinitions', fonts);
      resolve({});
    });
  },
};
const mutations = {
  setSelectedLayoutNode(state, nodeName) {
    state.selectedLayoutNode = nodeName;
  },
  setColors(state, colorArray) {
    state.colors = Object.assign([], colorArray);
  },
  setTemplateId(state, id) {
    state.id = id;
  },
  setName(state, name) {
    state.name = name;
  },
  setSubject(state, subject) {
    state.subject = subject;
  },
  setPreview(state, preview) {
    state.preview = preview;
  },
  setRootElement(state, rootElement) {
    state.rootElement = Object.assign({}, rootElement);
  },
  setBackgroundElement(state, backgroundElement) {
    state.backgroundElement = Object.assign({}, backgroundElement);
  },
  setHeaderElements(state, headerElements) {
    state.headerElements = Object.assign([], headerElements);
  },
  addBodyElement(state, meta) {
    state.bodyElements[meta.index].blocks[meta.column] = meta.obj;
  },
  setBodyElements(state, bodyElements) {
    state.bodyElements = Object.assign([], bodyElements);
  },
  setFooterElements(state, footerElements) {
    state.footerElements = Object.assign([], footerElements);
  },
  setTestEmail(state, newEmails) {
    state.testEmails = Object.assign([], newEmails);
  },
  setTestPhoneNumbers(state, phoneNumbers) {
    state.testPhoneNumbers = Object.assign([], phoneNumbers);
  },
  setBlockMeta(state, source) {
    const meta = Object.assign({}, source);

    const objMap = [
      {
        i: 'header',
        n: 'headerElements',
        r: false,
      },
      { i: 'body', n: 'bodyElements', r: false },
      {
        i: 'footer',
        n: 'footerElements',
        r: false,
      },
      {
        i: 'background',
        n: 'backgroundElement',
        r: true,
      },
    ];

    let row = undefined;
    for (const p of objMap) {
      if (meta.section !== p.i) continue;
      if (!p.r) {
        row = Object.assign({}, state[p.n][meta.row]);
      } else {
        row = Object.assign({}, state[p.n]);
      }
    }

    if (
      meta.data.useBackground &&
      meta.data.font &&
      meta.section === 'background'
    ) {
      row.font = meta.data.font;
    }
    if (
      meta.data.useBackground &&
      meta.data.width &&
      meta.section === 'background'
    ) {
      row.width = meta.data.width;
    }
    if (
      meta.data.useBackground &&
      meta.data.mutateDarkMode &&
      meta.section === 'background'
    ) {
      row.darkMode = meta.data.darkMode;
    }

    if (
      meta.data.useBackground &&
      meta.data.contentColor &&
      meta.section === 'background'
    ) {
      row.contentBackground = meta.data.contentColor;
    }
    if (
      meta.data.useBackground &&
      meta.data.mutateAdvancedEditor &&
      meta.section === 'background'
    ) {
      row.advancedEditor = meta.data.advancedEditor;
    }
    if (
      meta.data.useBackground &&
      meta.data.publish &&
      meta.section === 'background'
    ) {
      row.publish = meta.data.publish;
      row.publishSlug = meta.data.publishSlug;
      row.publishTitle = meta.data.publishTitle;
      row.publishDescription = meta.data.publishDescription;
    }

    if (meta.data.text) {
      if (meta.section === 'background') {
        row.text = meta.data.text;
      } else {
        row.blocks[meta.column].text = meta.data.text;
      }
    }
    if (meta.data.image && meta.data.canva === undefined) {
      if (meta.section === 'background') {
        row.image = meta.data.image;
      } else {
        delete row.blocks[meta.column].text;
        row.blocks[meta.column].image = meta.data.image;

        // Width
        row.blocks[meta.column].imageWidth = meta.data.imageWidth;
        row.blocks[meta.column].imageHeight = meta.data.imageHeight;
        // Preferred Width / Height
        row.blocks[meta.column].imagePreferredWidth =
          meta.data.imagePreferredWidth;
        row.blocks[meta.column].imagePreferredHeight =
          meta.data.imagePreferredHeight;
      }
    }
    // All attributions for Canva need to be here for store.
    if (meta.data.canva) {
      if (meta.section === 'background') {
        row.image = meta.data.image;
      } else {
        delete row.blocks[meta.column].text;
        row.blocks[meta.column].image = meta.data.image;
        row.blocks[meta.column].canva = meta.data.canva;

        // Width
        row.blocks[meta.column].imageWidth = meta.data.imageWidth;
        row.blocks[meta.column].imageHeight = meta.data.imageHeight;
        // Preferred Width / Height
        row.blocks[meta.column].imagePreferredWidth = meta.data.imageWidth;
        row.blocks[meta.column].imagePreferredHeight = meta.data.imageHeight;
      }
    }

    if (meta.data.video) {
      row.blocks[meta.column].video = meta.data.video;

      // screenshots - this is a nested object
      row.blocks[meta.column].screenshots = meta.data.screenshots;
      // Width
      row.blocks[meta.column].imageWidth = meta.data.imageWidth;
      row.blocks[meta.column].imageHeight = meta.data.imageHeight;
      row.blocks[meta.column].imageHeight = meta.data.imageHeight;
      // Preferred Width / Height
      row.blocks[meta.column].imagePreferredWidth =
        meta.data.imagePreferredWidth;
      row.blocks[meta.column].imagePreferredHeight =
        meta.data.imagePreferredHeight;
    }
    if (meta.data.imageSize) {
      // Width
      row.blocks[meta.column].imageWidth = meta.data.imageWidth;
      row.blocks[meta.column].imageHeight = meta.data.imageHeight;
      // Preferred Width / Height
      row.blocks[meta.column].imagePreferredWidth =
        meta.data.imagePreferredWidth;
      row.blocks[meta.column].imagePreferredHeight =
        meta.data.imagePreferredHeight;
    }

    if (meta.data.imageRetina) {
      // Retina
      if (row.blocks[meta.column].image != undefined)
        row.blocks[meta.column].imageRetina = meta.data.retina;
    }
    if (meta.data.mutateExternalSource) {
      // Raw Source
      row.blocks[meta.column].imageExternalSource =
        meta.data.imageExternalSource;
      row.blocks[meta.column].imageUseExternalSource =
        meta.data.imageUseExternalSource;
    }
    if (meta.data.hasOwnProperty('altTag')) {
      // ALT Tag
      if (row.blocks[meta.column].image != undefined) {
        row.blocks[meta.column].imageAltTag = meta.data.altTag;
      }
    }
    // Remove Image
    if (meta.data.removeImage) {
      row.blocks[meta.column].image = null;
      row.blocks[meta.column].text = null;
    }
    // Remove Image
    if (meta.data.removeVideo) {
      row.blocks[meta.column].video = null;
      row.blocks[meta.column].text = null;
    }
    // Modal Setup
    if (meta.data.useModal) {
      row.modalWidth = meta.data.modalWidth;
      row.modalEnableLeft = meta.data.modalEnableLeft;
      row.modalEnableRight = meta.data.modalEnableRight;
      row.modalWidthLeft = meta.data.modalWidthLeft;
      row.modalWidthRight = meta.data.modalWidthRight;
      row.interestOnComplete = meta.data.interestOnComplete;

      row.modalGroupId = meta.data.modalGroupId;
      row.modalWaitTime = meta.data.modalWaitTime;
      row.modalFormatType = meta.data.modalFormatType;
      row.modalStrategy = meta.data.modalStrategy;
    }
    if (meta.data.padding) {
      if (meta.section === 'background') {
        row.padding = Object.assign({}, meta.data.padding);
      } else {
        row.blocks[meta.column].padding = Object.assign({}, meta.data.padding);
      }
    }
    if (meta.data.margin) {
      if (meta.section === 'background') {
        row.margin = Object.assign({}, meta.data.margin);
      } else {
        row.blocks[meta.column].margin = Object.assign({}, meta.data.margin);
      }
    }
    if (meta.data.background) {
      if (meta.section === 'background') {
        row.background = meta.data.background;
      } else {
        row.blocks[meta.column].background = meta.data.background;
      }
    }
    if (meta.data.frameBackgroundColor) {
      if (meta.section === 'background') {
        row.frameBackgroundColor = meta.data.frameBackgroundColor;
      } else {
        row.blocks[meta.column].frameBackgroundColor =
          meta.data.frameBackgroundColor;
      }
    }
    if (meta.data.hasOwnProperty('textColor')) {
      if (meta.section === 'background') {
        row.textColor = meta.data.textColor;
      } else {
        row.blocks[meta.column].textColor = meta.data.textColor;
      }
    }
    if (meta.data.hasOwnProperty('textType')) {
      if (meta.section === 'background') {
        row.textType = meta.data.textType;
      } else {
        row.blocks[meta.column].textType = meta.data.textType;
      }
    }
    if (meta.data.hasOwnProperty('textSize')) {
      if (meta.section === 'background') {
        row.textSize = meta.data.textSize;
      } else {
        row.blocks[meta.column].textSize = meta.data.textSize;
      }
    }
    if (meta.data.hasOwnProperty('textAlign')) {
      if (meta.section === 'background') {
        row.textAlign = meta.data.textAlign;
      } else {
        row.blocks[meta.column].textAlign = meta.data.textAlign;
      }
    }
    if (meta.data.hasOwnProperty('textFont') && meta.section !== 'background') {
      row.blocks[meta.column].textFont = meta.data.textFont;
    }
    // Lines
    if (
      meta.data.hasOwnProperty('lineHeight') &&
      meta.section !== 'background'
    ) {
      row.blocks[meta.column].lineHeight = meta.data.lineHeight;
    }
    if (
      meta.data.hasOwnProperty('lineWidth') &&
      meta.section !== 'background'
    ) {
      row.blocks[meta.column].lineWidth = meta.data.lineWidth;
    }

    // Buttons
    if (
      meta.data.hasOwnProperty('borderRadius') &&
      meta.section !== 'background'
    ) {
      row.blocks[meta.column].borderRadius = meta.data.borderRadius;
    }

    // Links
    if (meta.data.hasOwnProperty('linkUrl') && meta.section !== 'background') {
      row.blocks[meta.column].linkUrl = meta.data.linkUrl;
    }
    if (meta.data.hasOwnProperty('linkUtm') && meta.section !== 'background') {
      row.blocks[meta.column].linkUtm = meta.data.linkUtm;
    }
    if (
      meta.data.hasOwnProperty('linkColor') &&
      meta.section !== 'background'
    ) {
      row.blocks[meta.column].linkColor = meta.data.linkColor;
    }
    if (
      meta.data.hasOwnProperty('linkUnderline') &&
      meta.section !== 'background'
    ) {
      row.blocks[meta.column].linkUnderline = meta.data.linkUnderline;
    }

    if (meta.data.field) {
      // one off to take the full tuple of values
      // TODO: we need to make better models to relate this stuff
      row.blocks[meta.column].field = meta.data.field;
      row.blocks[meta.column].required = meta.data.required;
    }
    // store row
    for (const p of objMap) {
      if (meta.section !== p.i) continue;
      let tmpObj = Object.assign([], state[p.n]);
      if (!p.r) {
        tmpObj[meta.row] = Object.assign({}, row);
        state[p.n] = Object.assign([], tmpObj);
      } else {
        tmpObj = Object.assign({}, row);
        state[p.n] = Object.assign({}, tmpObj);
      }
    }
  },
  setNameShown(state, openState) {
    state.showName = openState ? true : false;
  },
  setControlPaneShown(state, openState) {
    const obj = Object.assign({}, state.controlPane);
    obj.show = openState ? true : false;
    state.controlPane = obj;
  },
  setSelectedBlock(state, block) {
    state.selectedBlock = block;
  },
  setSelectedBlockSettings(state, settings) {
    state.selectedBlockSettings = Object.assign({}, settings);
  },
  setFontDefinitions(state, fonts) {
    state.fontDefinitions = Object.assign([], fonts);
  },
};

const arrayMove = (originArray, oldIndex, newIndex) => {
  const sourceArray = originArray.map((item) => ({ ...item }));

  // Adjust negative indices
  oldIndex = oldIndex < 0 ? sourceArray.length + oldIndex : oldIndex;
  newIndex = newIndex < 0 ? sourceArray.length + newIndex : newIndex;

  // If new index is beyond the array bounds, pad with undefined values
  if (newIndex >= sourceArray.length) {
    const numToAdd = newIndex - sourceArray.length + 1;
    sourceArray.push(...Array(numToAdd).fill(undefined));
  }

  const [removedItem] = sourceArray.splice(oldIndex, 1);
  sourceArray.splice(newIndex, 0, removedItem);

  return sourceArray;
};

export const duplicateArrayIndex = (arr, indexToDuplicate) => {
  const deepCopy = (obj) => {
    if (typeof obj !== 'object' || obj === null) {
      return obj;
    }
    let copy;
    if (Array.isArray(obj)) {
      copy = [];
      for (let i = 0; i < obj.length; i++) {
        copy.push(deepCopy(obj[i]));
      }
      return copy;
    }
    copy = {};
    for (let key in obj) {
      if (obj.hasOwnProperty(key)) {
        copy[key] = deepCopy(obj[key]);
      }
    }
    return copy;
  };

  const sourceArray = deepCopy(arr);
  sourceArray.splice(
    indexToDuplicate,
    0,
    deepCopy(sourceArray[indexToDuplicate])
  );
  return sourceArray;
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
